import { z } from 'zod';
import { ProtocolEnvelopeBaseSchema } from './envelope.js';
// --- Shared room/match domain types (wire-level DTOs) ---
export const RoomPhaseSchema = z.enum([
    'LOBBY',
    'CONFIGURING',
    'READY_CHECK',
    'STARTING',
    'IN_MATCH',
    'PAUSED',
    'COMPLETED',
    'CLOSED',
]);
export const MemberStatusSchema = z.enum([
    'INVITED',
    'JOINING',
    'CONNECTED',
    'READY',
    'PLAYING',
    'DISCONNECTED',
    'RECONNECTING',
    'LEFT',
]);
export const RoomMemberSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    status: MemberStatusSchema,
    isOwner: z.boolean(),
    seatIndex: z.number().int().nonnegative().optional(),
    ready: z.boolean().optional(),
});
// --- Connection payload schemas ---
export const ClientHelloPayloadSchema = z.object({
    sessionToken: z.string(),
    appVersion: z.string(),
    buildId: z.string(),
    protocolVersion: z.string(),
    gameModuleVersions: z.record(z.string()),
    capabilityFlags: z.array(z.string()),
});
export const SessionAcceptedPayloadSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    serverMs: z.number().int().nonnegative(),
});
export const SessionRejectedPayloadSchema = z.object({
    reason: z.string(),
    message: z.string(),
});
export const PingPayloadSchema = z.object({
    clientMs: z.number().int().nonnegative(),
});
export const PongPayloadSchema = z.object({
    clientMs: z.number().int().nonnegative(),
    serverMs: z.number().int().nonnegative(),
});
export const ErrorPayloadSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
});
// --- Room payload schemas ---
export const RoomJoinPayloadSchema = z.object({
    roomCode: z.string().min(1).max(16),
    resumeToken: z.string().optional(),
    lastAppliedServerSequence: z.number().int().nonnegative().optional(),
    lastStateHash: z.string().optional(),
});
export const RoomJoinedPayloadSchema = z.object({
    roomId: z.string(),
    roomCode: z.string(),
    phase: RoomPhaseSchema,
    ownerId: z.string(),
    members: z.array(RoomMemberSchema),
    config: z.record(z.unknown()),
    matchId: z.string().optional(),
    gameId: z.string().optional(),
    resumeToken: z.string(),
    serverSequence: z.number().int().nonnegative(),
    serverMs: z.number().int().nonnegative(),
});
export const RoomLeavePayloadSchema = z.object({});
export const RoomMemberJoinedPayloadSchema = z.object({
    member: RoomMemberSchema,
    serverMs: z.number().int().nonnegative(),
});
export const RoomMemberLeftPayloadSchema = z.object({
    userId: z.string(),
    serverMs: z.number().int().nonnegative(),
});
export const RoomReadySetPayloadSchema = z.object({
    ready: z.boolean(),
});
export const RoomConfigUpdatePayloadSchema = z.object({
    config: z.record(z.unknown()),
});
export const RoomSnapshotPayloadSchema = z.object({
    roomId: z.string(),
    roomCode: z.string(),
    phase: RoomPhaseSchema,
    ownerId: z.string(),
    members: z.array(RoomMemberSchema),
    config: z.record(z.unknown()),
    matchId: z.string().optional(),
    serverSequence: z.number().int().nonnegative(),
    stateHash: z.string(),
    serverMs: z.number().int().nonnegative(),
});
// --- Match payload schemas ---
export const MatchStartRequestPayloadSchema = z.object({
    gameId: z.string(),
});
export const MatchScheduledPayloadSchema = z.object({
    matchId: z.string(),
    startAtServerMs: z.number().int().nonnegative(),
});
export const MatchStartedPayloadSchema = z.object({
    matchId: z.string(),
    gameId: z.string(),
    startAtServerMs: z.number().int().nonnegative(),
    randomSeed: z.number().int(),
    initialState: z.unknown(),
    stateHash: z.string(),
    serverSequence: z.number().int().nonnegative(),
});
export const GameCommandPayloadSchema = z.object({
    matchId: z.string(),
    gamePayload: z.unknown(),
});
export const GameEventPayloadSchema = z.object({
    eventId: z.string(),
    matchId: z.string(),
    eventType: z.string(),
    gamePayload: z.unknown(),
    stateHash: z.string().optional(),
    serverMs: z.number().int().nonnegative(),
});
export const StateHashPayloadSchema = z.object({
    matchId: z.string(),
    serverSequence: z.number().int().nonnegative(),
    hash: z.string(),
});
export const ResyncRequestPayloadSchema = z.object({
    matchId: z.string(),
    lastAppliedServerSequence: z.number().int().nonnegative(),
    lastStateHash: z.string(),
});
export const StateSnapshotPayloadSchema = z.object({
    matchId: z.string(),
    serverSequence: z.number().int().nonnegative(),
    state: z.unknown(),
    stateHash: z.string(),
    serverMs: z.number().int().nonnegative(),
});
export const MatchPausedPayloadSchema = z.object({
    matchId: z.string(),
    reason: z.string().optional(),
    serverMs: z.number().int().nonnegative(),
});
export const MatchResumedPayloadSchema = z.object({
    matchId: z.string(),
    serverMs: z.number().int().nonnegative(),
});
export const MatchCompletedPayloadSchema = z.object({
    matchId: z.string(),
    result: z.unknown(),
    finalStateHash: z.string(),
    serverMs: z.number().int().nonnegative(),
});
export const MatchAbortedPayloadSchema = z.object({
    matchId: z.string(),
    reason: z.string(),
    serverMs: z.number().int().nonnegative(),
});
function parsePayload(schema, raw) {
    const result = schema.safeParse(raw);
    if (result.success)
        return { ok: true, value: result.data };
    return { ok: false, error: result.error.message };
}
function buildEnvelope(base, messageType, payload) {
    return {
        protocolVersion: base.protocolVersion,
        messageId: base.messageId,
        messageType,
        roomId: base.roomId,
        matchId: base.matchId,
        senderPublicId: base.senderPublicId,
        clientSequence: base.clientSequence,
        serverSequence: base.serverSequence,
        sentAtClientMs: base.sentAtClientMs,
        sentAtServerMs: base.sentAtServerMs,
        acknowledgement: base.acknowledgement,
        payload,
    };
}
function parseBase(raw) {
    let json;
    try {
        json = JSON.parse(raw);
    }
    catch {
        return { ok: false, error: 'Invalid JSON' };
    }
    const result = ProtocolEnvelopeBaseSchema.safeParse(json);
    if (!result.success)
        return { ok: false, error: result.error.message };
    return { ok: true, base: result.data };
}
// --- Public parse functions ---
export function parseC2SMessage(raw) {
    const baseResult = parseBase(raw);
    if (!baseResult.ok)
        return baseResult;
    const { base } = baseResult;
    switch (base.messageType) {
        case 'CLIENT_HELLO': {
            const r = parsePayload(ClientHelloPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'CLIENT_HELLO', r.value) };
        }
        case 'ROOM_JOIN': {
            const r = parsePayload(RoomJoinPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_JOIN', r.value) };
        }
        case 'ROOM_LEAVE': {
            const r = parsePayload(RoomLeavePayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_LEAVE', r.value) };
        }
        case 'ROOM_READY_SET': {
            const r = parsePayload(RoomReadySetPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_READY_SET', r.value) };
        }
        case 'ROOM_CONFIG_UPDATE': {
            const r = parsePayload(RoomConfigUpdatePayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_CONFIG_UPDATE', r.value) };
        }
        case 'MATCH_START_REQUEST': {
            const r = parsePayload(MatchStartRequestPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'MATCH_START_REQUEST', r.value) };
        }
        case 'GAME_COMMAND': {
            const r = parsePayload(GameCommandPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'GAME_COMMAND', r.value) };
        }
        case 'RESYNC_REQUEST': {
            const r = parsePayload(ResyncRequestPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'RESYNC_REQUEST', r.value) };
        }
        case 'PING': {
            const r = parsePayload(PingPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'PING', r.value) };
        }
        default:
            return { ok: false, error: `Unknown C2S messageType: ${base.messageType}` };
    }
}
export function parseS2CMessage(raw) {
    const baseResult = parseBase(raw);
    if (!baseResult.ok)
        return baseResult;
    const { base } = baseResult;
    switch (base.messageType) {
        case 'SESSION_ACCEPTED': {
            const r = parsePayload(SessionAcceptedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'SESSION_ACCEPTED', r.value) };
        }
        case 'SESSION_REJECTED': {
            const r = parsePayload(SessionRejectedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'SESSION_REJECTED', r.value) };
        }
        case 'ROOM_JOINED': {
            const r = parsePayload(RoomJoinedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_JOINED', r.value) };
        }
        case 'ROOM_MEMBER_JOINED': {
            const r = parsePayload(RoomMemberJoinedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_MEMBER_JOINED', r.value) };
        }
        case 'ROOM_MEMBER_LEFT': {
            const r = parsePayload(RoomMemberLeftPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_MEMBER_LEFT', r.value) };
        }
        case 'ROOM_SNAPSHOT': {
            const r = parsePayload(RoomSnapshotPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ROOM_SNAPSHOT', r.value) };
        }
        case 'MATCH_SCHEDULED': {
            const r = parsePayload(MatchScheduledPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'MATCH_SCHEDULED', r.value) };
        }
        case 'MATCH_STARTED': {
            const r = parsePayload(MatchStartedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'MATCH_STARTED', r.value) };
        }
        case 'GAME_EVENT': {
            const r = parsePayload(GameEventPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'GAME_EVENT', r.value) };
        }
        case 'STATE_HASH': {
            const r = parsePayload(StateHashPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'STATE_HASH', r.value) };
        }
        case 'STATE_SNAPSHOT': {
            const r = parsePayload(StateSnapshotPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'STATE_SNAPSHOT', r.value) };
        }
        case 'MATCH_PAUSED': {
            const r = parsePayload(MatchPausedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'MATCH_PAUSED', r.value) };
        }
        case 'MATCH_RESUMED': {
            const r = parsePayload(MatchResumedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'MATCH_RESUMED', r.value) };
        }
        case 'MATCH_COMPLETED': {
            const r = parsePayload(MatchCompletedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'MATCH_COMPLETED', r.value) };
        }
        case 'MATCH_ABORTED': {
            const r = parsePayload(MatchAbortedPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'MATCH_ABORTED', r.value) };
        }
        case 'PONG': {
            const r = parsePayload(PongPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'PONG', r.value) };
        }
        case 'ERROR': {
            const r = parsePayload(ErrorPayloadSchema, base.payload);
            if (!r.ok)
                return r;
            return { ok: true, message: buildEnvelope(base, 'ERROR', r.value) };
        }
        default:
            return { ok: false, error: `Unknown S2C messageType: ${base.messageType}` };
    }
}
//# sourceMappingURL=messages.js.map